import { useEffect } from 'react';
import { api } from '@/lib/api';

/**
 * ExperienceApplier — small effect that fetches /theme and /readability on mount
 * and writes the values as CSS variables + a data-theme attribute.
 * Also listens to `pg:experience-updated` custom events.
 *
 * Runs alongside the existing SiteSettingsApplier without touching it.
 */
export default function ExperienceApplier() {
  useEffect(() => {
    let alive = true;
    async function apply() {
      try {
        const [{ data: theme }, { data: read }] = await Promise.all([
          api.get('/theme').catch(() => ({ data: null })),
          api.get('/readability').catch(() => ({ data: null })),
        ]);
        if (!alive) return;
        const root = document.documentElement;
        if (theme) {
          root.style.setProperty('--pg-theme-primary',   theme.color_primary);
          root.style.setProperty('--pg-theme-secondary', theme.color_secondary);
          root.style.setProperty('--pg-theme-accent',    theme.color_accent);
          root.style.setProperty('--pg-theme-ink',       theme.color_ink);
          root.style.setProperty('--pg-theme-ink-muted', theme.color_ink_muted);
          root.style.setProperty('--pg-theme-ink-faint', theme.color_ink_faint);
          root.style.setProperty('--pg-theme-bg',        theme.color_bg);
          root.style.setProperty('--pg-theme-bg-elev',   theme.color_bg_elev);
          root.style.setProperty('--pg-theme-border',    theme.color_border);
          root.style.setProperty('--pg-theme-button-radius', theme.button_radius);
          root.style.setProperty('--pg-theme-radius-sm', theme.radius_sm);
          root.style.setProperty('--pg-theme-radius-md', theme.radius_md);
          root.style.setProperty('--pg-theme-radius-lg', theme.radius_lg);
          root.style.setProperty('--pg-theme-shadow-sm', theme.shadow_sm);
          root.style.setProperty('--pg-theme-shadow-md', theme.shadow_md);
          root.style.setProperty('--pg-theme-line-height', String(theme.line_height));
          root.style.setProperty('--pg-theme-letter-spacing', theme.letter_spacing);
          root.style.setProperty('--pg-theme-font-serif', theme.font_family_serif);
          root.style.setProperty('--pg-theme-font-sans',  theme.font_family_sans);
          root.style.setProperty('--pg-theme-font-mono',  theme.font_family_mono);
          root.style.setProperty('--pg-theme-base-size', `${theme.base_font_size_px}px`);
          root.style.setProperty('--pg-theme-heading-weight', String(theme.heading_font_weight));
          root.style.setProperty('--pg-theme-body-weight',    String(theme.body_font_weight));
          root.style.setProperty('--pg-theme-spacing', theme.global_spacing);
          root.setAttribute('data-pg-button-style', theme.button_style || 'solid');
          root.setAttribute('data-pg-mode', theme.dark_mode_default ? 'dark' : 'light');
        }
        if (read) {
          root.style.setProperty('--pg-read-brightness', `${read.font_brightness_pct}%`);
          root.style.setProperty('--pg-read-contrast-boost', `${read.font_contrast_boost_pct}%`);
          root.style.setProperty('--pg-read-overlay-color', read.overlay_color);
          root.style.setProperty('--pg-read-overlay-opacity', `${read.overlay_opacity_pct}%`);
          root.style.setProperty('--pg-read-blur', `${read.background_blur_px}px`);
          root.setAttribute('data-pg-readability', read.enabled ? 'on' : 'off');
          root.setAttribute('data-pg-overlay', read.overlay_enabled ? 'on' : 'off');
          root.setAttribute('data-pg-pills', read.pill_backgrounds ? 'on' : 'off');
        }
      } catch { /* silent */ }
    }
    apply();
    const onEvent = () => apply();
    window.addEventListener('pg:experience-updated', onEvent);
    return () => { alive = false; window.removeEventListener('pg:experience-updated', onEvent); };
  }, []);
  return null;
}
